import type React from "react";
import { ParseSheetCommandError, parseSheetCommandBatch } from "../commandParser";
import { applySheetCommandBatch, SheetCommandExecutionError } from "../sheetCommandBatch";
import type { SheetCommandDraft } from "../sheetCommandBatch";
import type { SheetAccountEntry } from "../../../types/sheetDocument";
import type { TAccountCatalogItem } from "../types";

interface UseAtomicSheetCommandProcessorArgs {
  nextAutoLetterIndexRef: React.MutableRefObject<number>;
  nextEntrySequenceRef: React.MutableRefObject<number>;
  tAccountsRef: React.MutableRefObject<SheetAccountEntry[]>;
  uberCommentsByLetterRef: React.MutableRefObject<Record<string, string>>;
  forceTwoDecimalDisplayRef: React.MutableRefObject<boolean>;
  documentTitleRef: React.MutableRefObject<string>;
  taccountCatalogRef: React.MutableRefObject<TAccountCatalogItem[]>;
  setTAccounts: React.Dispatch<React.SetStateAction<SheetAccountEntry[]>>;
  setUberCommentsByLetter: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setForceTwoDecimalDisplay: React.Dispatch<React.SetStateAction<boolean>>;
  setDocumentTitle: React.Dispatch<React.SetStateAction<string>>;
  setExecutedCommandCount: React.Dispatch<React.SetStateAction<number>>;
  setTAccountCatalog?: (catalog: TAccountCatalogItem[]) => void;
  maxTAccountsPerDocument: number | null;
  maxTransactionsPerDocument?: number | null;
  onTAccountVisualizationLimitReached: (limit: number) => void;
  onTransactionLimitReached?: (limit: number) => void;
  onHelpCommand?: () => void;
  showError: (message: string) => void;
  clearError: () => void;
}

export interface PreparedSheetCommandBatch {
  draft: SheetCommandDraft;
  commandCount: number;
}

export function useAtomicSheetCommandProcessor({
  nextAutoLetterIndexRef,
  nextEntrySequenceRef,
  tAccountsRef,
  uberCommentsByLetterRef,
  forceTwoDecimalDisplayRef,
  documentTitleRef,
  taccountCatalogRef,
  setTAccounts,
  setUberCommentsByLetter,
  setForceTwoDecimalDisplay,
  setDocumentTitle,
  setExecutedCommandCount,
  setTAccountCatalog,
  maxTAccountsPerDocument,
  maxTransactionsPerDocument = null,
  onTAccountVisualizationLimitReached,
  onTransactionLimitReached,
  onHelpCommand,
  showError,
  clearError,
}: UseAtomicSheetCommandProcessorArgs) {
  const prepareEntrySubmit = (
    value: string,
    catalog: TAccountCatalogItem[] = taccountCatalogRef.current,
  ): PreparedSheetCommandBatch | null => {
    try {
      const commands = parseSheetCommandBatch(value);
      const draft = applySheetCommandBatch({
        commands,
        initialDraft: {
          tAccounts: tAccountsRef.current,
          uberCommentsByLetter: uberCommentsByLetterRef.current,
          forceTwoDecimalDisplay: forceTwoDecimalDisplayRef.current,
          documentTitle: documentTitleRef.current,
          nextAutoLetterIndex: nextAutoLetterIndexRef.current,
          nextEntrySequence: nextEntrySequenceRef.current,
          catalog,
        },
        maxTAccountsPerDocument,
        maxTransactionsPerDocument,
      });
      return { draft, commandCount: commands.length };
    } catch (error) {
      if (error instanceof SheetCommandExecutionError && error.limit) {
        if (error.limit.kind === "taccounts") {
          onTAccountVisualizationLimitReached(error.limit.value);
        } else {
          onTransactionLimitReached?.(error.limit.value);
        }
        return null;
      }
      const message = error instanceof ParseSheetCommandError || error instanceof Error
        ? error.message
        : "Invalid command.";
      showError(message);
      return null;
    }
  };

  const commitEntrySubmit = ({ draft, commandCount }: PreparedSheetCommandBatch): void => {
    tAccountsRef.current = draft.tAccounts;
    uberCommentsByLetterRef.current = draft.uberCommentsByLetter;
    forceTwoDecimalDisplayRef.current = draft.forceTwoDecimalDisplay;
    documentTitleRef.current = draft.documentTitle;
    taccountCatalogRef.current = draft.catalog;
    nextAutoLetterIndexRef.current = draft.nextAutoLetterIndex;
    nextEntrySequenceRef.current = draft.nextEntrySequence;

    setTAccounts(draft.tAccounts);
    setUberCommentsByLetter(draft.uberCommentsByLetter);
    setForceTwoDecimalDisplay(draft.forceTwoDecimalDisplay);
    setDocumentTitle(draft.documentTitle);
    setTAccountCatalog?.(draft.catalog);
    setExecutedCommandCount((previousCount) => previousCount + commandCount);
    clearError();
    if (draft.showHelp) onHelpCommand?.();
  };

  const handleEntrySubmit = (value: string): boolean => {
    const prepared = prepareEntrySubmit(value);
    if (!prepared) return false;
    commitEntrySubmit(prepared);
    return true;
  };

  return { prepareEntrySubmit, commitEntrySubmit, handleEntrySubmit };
}